import { useState, useMemo } from 'react';

/**
 * Reusable filter hook with date range and search capabilities
 * @param {Array} items - Array of items to filter
 * @param {Object} config - Configuration for filters
 * @returns {Object} Filter state and filtered items
 */
export const useFilters = (items = [], config = {}) => {
  const {
    searchFields = ['name', 'title', 'description'], // Fields to search in
    dateField = 'createdAt', // Field to use for date filtering
  } = config;

  const [filters, setFilters] = useState({
    search: '',
    startDate: '',
    endDate: '',
    category: 'all',
    status: 'all',
  });

  // Apply all filters
  const filteredItems = useMemo(() => {
    // Ensure items is always an array
    if (!Array.isArray(items)) {
      return [];
    }
    let result = [...items];

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter((item) => {
        return searchFields.some((field) => {
          const value = item[field];
          return value && String(value).toLowerCase().includes(searchLower);
        });
      });
    }

    // Date range filter
    if (filters.startDate || filters.endDate) {
      result = result.filter((item) => {
        const itemDate = new Date(item[dateField]);
        const startDate = filters.startDate ? new Date(filters.startDate) : null;
        const endDate = filters.endDate ? new Date(filters.endDate) : null;

        if (startDate && itemDate < startDate) return false;
        if (endDate && itemDate > endDate) return false;

        return true;
      });
    }

    // Category filter
    if (filters.category && filters.category !== 'all') {
      result = result.filter((item) => item.category === filters.category);
    }

    // Status filter
    if (filters.status && filters.status !== 'all') {
      result = result.filter((item) => item.status === filters.status);
    }

    return result;
  }, [items, filters, searchFields, dateField]);

  // Update individual filter
  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Update multiple filters at once
  const updateFilters = (newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  // Reset all filters
  const resetFilters = () => {
    setFilters({
      search: '',
      startDate: '',
      endDate: '',
      category: 'all',
      status: 'all',
    });
  };

  return {
    filters,
    filteredItems,
    updateFilter,
    updateFilters,
    resetFilters,
    hasActiveFilters:
      filters.search ||
      filters.startDate ||
      filters.endDate ||
      (filters.category && filters.category !== 'all') ||
      (filters.status && filters.status !== 'all'),
  };
};

export default useFilters;
